import { memo, useMemo, useState } from "react";
import { Paper, Skeleton, Autocomplete, TextField, Chip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import { dashboardsAjax } from "../../../ajax/DashboardsAjax";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { RootState } from "../../../state/store";
import { useAppDispatch } from "../../../state/hooks";
import { setTargetCategory } from "../../../state/reducers/dashboardReducer";
import { TARGET_CATEGORY_NAMES, TargetCategoryName } from "../../../types/TargetCategory";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { buildTargetGauges, elapsedFraction, toggleGaugeKey } from "./targetGauges";
import { GaugeMeter } from "./GaugeMeter";
import { materialMovementQuery, plantStatusQuery } from "./dashboardQueries";

type TargetsCardProps = {
  isFullScreen?: boolean;
};

export const TargetsCard: React.FC<TargetsCardProps> = memo(({ isFullScreen }) => {
  const { selectedDate, selectedShift } = useShiftTimes();
  const dispatch = useAppDispatch();
  const category = useSelector((state: RootState) => state.dashboard.targetCategory);
  const [shownKeys, setShownKeys] = useState<string[] | null>(null);

  const dateIso = selectedDate.toISODate();
  const queryEnabled = selectedDate.isValid && !!selectedShift;

  const { data: targetsData, isLoading: targetsLoading } = useQuery({
    queryKey: ["dashboard", "targets", dateIso, category],
    queryFn: async () => {
      const res = await dashboardsAjax.getTargetsCardData(selectedDate, category);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: queryEnabled,
  });

  const { data: materialMovement = null, isLoading: movementLoading } = useQuery({
    ...materialMovementQuery(selectedDate, selectedShift),
    enabled: queryEnabled,
  });

  const { isLoading: plantLoading, dataUpdatedAt } = useQuery({
    ...plantStatusQuery(selectedDate, selectedShift, false),
    enabled: queryEnabled,
  });

  const gauges = useMemo(() => {
    if (!targetsData) return [];

    const now = dataUpdatedAt > 0 ? DateTime.fromMillis(dataUpdatedAt) : DateTime.now();
    const elapsed = elapsedFraction(targetsData.dayStartUtc, targetsData.dayEndUtc, now);

    return buildTargetGauges(targetsData.entries, { materialMovement }, elapsed);
  }, [targetsData, materialMovement, dataUpdatedAt]);

  const gaugeKeys = useMemo(() => gauges.map(gauge => gauge.key), [gauges]);

  const shown = useMemo(
    () => (shownKeys === null ? gauges : gauges.filter(gauge => shownKeys.includes(gauge.key))),
    [gauges, shownKeys]
  );

  const showSkeleton = targetsLoading || movementLoading || plantLoading || !queryEnabled;
  const isEmpty = !showSkeleton && gauges.length === 0;

  return (
    <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col gap-3">
      <div className="flex flex-wrap gap-2 px-3 pt-3">
        <Autocomplete
          size="small"
          className="min-w-[150px] flex-1"
          options={TARGET_CATEGORY_NAMES as readonly TargetCategoryName[]}
          value={category}
          disableClearable
          onChange={(_, value) => value && dispatch(setTargetCategory(value))}
          renderInput={(params) => <TextField {...params} label="Compare against" />}
        />
        <div className="flex flex-wrap items-center gap-2 flex-[4] min-w-[200px]">
          {gauges.map(gauge => {
            const isShown = shownKeys === null || shownKeys.includes(gauge.key);

            return (
              <Chip
                key={gauge.key}
                label={gauge.label}
                size="small"
                aria-pressed={isShown}
                onClick={() => setShownKeys(toggleGaugeKey(shownKeys, gaugeKeys, gauge.key))}
                className={isShown
                  ? "!bg-theme-primary !text-theme-primary-contrast !border !border-theme-primary"
                  : "!bg-theme-surface !text-theme-text-muted !border !border-theme-border"}
              />
            );
          })}
        </div>
      </div>

      {showSkeleton && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 animate-fade-in pt-3 px-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl px-3 py-6 bg-theme-table-heading">
              <Skeleton animation="wave" variant="text" width="60%" height={22} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="80%" height={40} className="bg-theme-table-row-even" />
            </div>
          ))}
        </div>
      )}

      {isEmpty && (
        <DashboardCardEmptyState
          isFullScreen={isFullScreen}
          title="No targets to compare"
          subtitle="No targets are set for this date, or the figures they compare against have not loaded."
        />
      )}

      {!showSkeleton && !isEmpty && shown.length > 0 && (
        <div
          className={`flex-1 overflow-y-auto grid gap-3 px-3 py-3 animate-fade-in ${isFullScreen ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3"} content-start`}
        >
          {shown.map(gauge => (
            <GaugeMeter
              isCardFullScreen={isFullScreen}
              key={gauge.key}
              gauge={gauge}
              onHide={() => setShownKeys(toggleGaugeKey(shownKeys, gaugeKeys, gauge.key))}
            />
          ))}
        </div>
      )}

      {!showSkeleton && !isEmpty && shown.length === 0 && (
        <p className="flex-1 flex items-center justify-center text-sm text-theme-text-muted">
          Pick a metric above to show it.
        </p>
      )}
    </Paper>
  );
});
